import React from 'react';
import PropTypes from 'prop-types';
import { CircularProgress, Typography } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { getBreakTypeName } from '../breakOptionsModalHelpers';
import { useTimeFormatters } from '../../../../../utils/Hooks/venue/useVenueTimeFormatters';
import {
  StepContentContainer,
  ModalTitle,
  SelectionDescription,
  BreaksList,
  BreakListItem,
  BreakListItemButton,
  BreakListItemText,
  LoadingContainer,
  EmptyStateContainer,
  WarningContainer,
  WarningText,
} from '../BreakOptionsModal.styles';

const WaiverBreakSelectionStep = ({ breaks, isLoadingBreaks, onBreakSelect }) => {
  const { formatTime } = useTimeFormatters();
  const sortedBreaks = breaks.slice().sort((a, b) => {
    if (a.scheduledStart && b.scheduledStart) {
      return new Date(a.scheduledStart) - new Date(b.scheduledStart);
    }
    return 0;
  });

  if (isLoadingBreaks) {
    return (
      <StepContentContainer>
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </StepContentContainer>
    );
  }

  if (sortedBreaks.length === 0) {
    return (
      <StepContentContainer>
        <ModalTitle variant="h6" component="h2">
          No Breaks Available
        </ModalTitle>
        <EmptyStateContainer>
          <Typography variant="body1">There are no available breaks to waive for this shift.</Typography>
        </EmptyStateContainer>
      </StepContentContainer>
    );
  }

  return (
    <StepContentContainer>
      <ModalTitle variant="h6" component="h2">
        Select Break to Waive
      </ModalTitle>

      <SelectionDescription variant="body2">
        Click on a break below to waive it. You will be asked to provide a reason in the next step.
      </SelectionDescription>

      {sortedBreaks.some((breakItem) => breakItem.canBeWaived === false && breakItem.waivingForbiddenReason) && (
        <WarningContainer>
          <WarningIcon color="warning" />
          <WarningText>
            <strong>Note:</strong> Some breaks below may cause violations if waived. These are marked with warning
            indicators.
          </WarningText>
        </WarningContainer>
      )}

      <BreaksList>
        {sortedBreaks.map((breakItem) => (
          <BreakListItem key={breakItem.id}>
            <BreakListItemButton onClick={() => onBreakSelect(breakItem)}>
              <BreakListItemText
                primary={`${getBreakTypeName(breakItem.type)}`}
                secondary={
                  breakItem.scheduledStart
                    ? `Scheduled for ${formatTime(new Date(breakItem.scheduledStart))}`
                    : 'No scheduled time'
                }
              />
              {breakItem.canBeWaived === false && breakItem.waivingForbiddenReason && (
                <WarningIcon color="warning" fontSize="small" />
              )}
            </BreakListItemButton>
          </BreakListItem>
        ))}
      </BreaksList>
    </StepContentContainer>
  );
};

WaiverBreakSelectionStep.propTypes = {
  breaks: PropTypes.array.isRequired,
  isLoadingBreaks: PropTypes.bool.isRequired,
  onBreakSelect: PropTypes.func.isRequired,
};

export default WaiverBreakSelectionStep;
